import { api, publicApi } from '../lib/api'
import type {
  Booking,
  BookingAvailability,
  BookingCreatePayload,
  BookingDetailResponse,
  BookingListResponse,
  BookingNotificationOutboxResponse,
  BookingNotificationOutboxRow,
  BookingQuotePayload,
  BookingQuoteResponse,
  BookingReview,
  BookingService,
  BookingServiceUpsertPayload,
  ExternalCalendarConnection,
  BookingStaffMember,
  BookingStaffHour,
  BookingStaffSpecialHour,
  BookingStaffTimeOff,
  BookingSettings,
  BookingSettingsUpdatePayload,
  StripeConnectStartResponse,
  StripeConnectStatus,
} from '../types/bookings'

export async function bookingAvailability(params: {
  host_type: 'business' | 'pro'
  slug?: string
  handle?: string
  date: string
  service_id?: number
  staff_mode?: 'ANY' | 'SPECIFIC'
  staff_id?: number
}): Promise<BookingAvailability> {
  const { data } = await publicApi.get<BookingAvailability>('/api/booking/availability/', { params })
  return data
}

export async function createBooking(payload: BookingCreatePayload): Promise<Booking> {
  const { data } = await publicApi.post<Booking>('/api/booking/book/', payload)
  return data
}

export async function quoteBooking(payload: BookingQuotePayload): Promise<BookingQuoteResponse> {
  const { data } = await publicApi.post<BookingQuoteResponse>('/api/booking/quote/', payload)
  return data
}

export async function getBookingDetail(bookingPublicId: string, token?: string): Promise<BookingDetailResponse> {
  const client = token ? publicApi : api
  const { data } = await client.get<BookingDetailResponse>(`/api/booking/${encodeURIComponent(bookingPublicId)}/`, {
    params: token ? { t: token } : undefined,
  })
  return data
}

export async function payBookingNow(bookingPublicId: string, token?: string): Promise<{ client_secret: string; booking: Booking }> {
  const client = token ? publicApi : api
  const { data } = await client.post<{ client_secret: string; booking: Booking }>(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/pay/`,
    {},
    {
      params: token ? { t: token } : undefined,
    }
  )
  return data
}

export async function getBookingReview(bookingPublicId: string, token?: string): Promise<BookingReview> {
  const client = token ? publicApi : api
  const { data } = await client.get<BookingReview>(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/review/`,
    { params: token ? { t: token } : undefined },
  )
  return data
}

export async function createBookingReview(
  bookingPublicId: string,
  payload: { rating: number; body?: string },
  token?: string,
): Promise<BookingReview> {
  const client = token ? publicApi : api
  const { data } = await client.post<BookingReview>(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/review/`,
    payload,
    { params: token ? { t: token } : undefined },
  )
  return data
}

export async function cancelBooking(bookingPublicId: string): Promise<Booking> {
  const { data } = await api.post<Booking>(`/api/booking/${encodeURIComponent(bookingPublicId)}/cancel/`, {})
  return data
}

export async function cancelBookingWithReason(bookingPublicId: string, reason: string): Promise<Booking> {
  const { data } = await api.post<Booking>(`/api/booking/${encodeURIComponent(bookingPublicId)}/cancel/`, { reason })
  return data
}

export async function rescheduleBooking(
  bookingPublicId: string,
  newStart: string,
  timezone?: string,
): Promise<Booking> {
  const payload: { new_start: string; timezone?: string } = { new_start: newStart }
  if (timezone) payload.timezone = timezone
  const { data } = await api.post<Booking>(`/api/booking/${encodeURIComponent(bookingPublicId)}/reschedule/`, payload)
  return data
}

export async function refundBooking(bookingPublicId: string): Promise<{ ok: boolean; booking_public_id: string; refund_id: string; refund_status: string; payment_status: string }> {
  const { data } = await api.post<{ ok: boolean; booking_public_id: string; refund_id: string; refund_status: string; payment_status: string }>(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/refund/`,
    {},
  )
  return data
}

export async function resendBookingConfirm(bookingPublicId: string): Promise<{ ok: boolean; enqueued: boolean }> {
  const { data } = await api.post<{ ok: boolean; enqueued: boolean }>(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/notify/confirm/`,
    {},
  )
  return data
}

export async function listBookingNotifications(bookingPublicId: string, limit = 20): Promise<BookingNotificationOutboxRow[]> {
  const { data } = await api.get<BookingNotificationOutboxResponse>(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/notifications/`,
    { params: { limit } },
  )
  return Array.isArray(data?.results) ? data.results : []
}

function parseFilenameFromDisposition(disposition: string | undefined, fallback: string): string {
  const raw = String(disposition || '')
  const match = raw.match(/filename="?([^";]+)"?/i)
  return match?.[1] || fallback
}

export async function downloadBookingIcs(bookingPublicId: string, kind: 'request' | 'cancel' = 'request'): Promise<void> {
  const resp = await api.get(
    `/api/booking/${encodeURIComponent(bookingPublicId)}/ics/`,
    { params: { kind }, responseType: 'blob' },
  )
  const contentType = String(resp.headers?.['content-type'] || 'text/calendar; charset=utf-8')
  const filename = parseFilenameFromDisposition(resp.headers?.['content-disposition'], `booking-${bookingPublicId}.ics`)
  const blob = new Blob([resp.data], { type: contentType })
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}

export async function getBusinessBookingSettings(slug: string): Promise<BookingSettings> {
  const { data } = await api.get<BookingSettings>(`/api/businesses/${encodeURIComponent(slug)}/booking/settings/`)
  return data
}

export async function listBookingExternalCalendars(params: { provider_type: 'BUSINESS' | 'PRO'; provider_public_id: string }): Promise<ExternalCalendarConnection[]> {
  const { data } = await api.get<{ results: ExternalCalendarConnection[] }>('/api/booking/external-calendars/', { params })
  return Array.isArray(data?.results) ? data.results : []
}

export async function startGoogleCalendarConnect(params: {
  provider_type: 'BUSINESS' | 'PRO'
  provider_id: number
  provider_public_id?: string
  return_url?: string
}): Promise<string> {
  const { data } = await api.get<{ url: string }>('/api/booking/external-calendars/google/start/', {
    params: {
      ...params,
      as_json: 1,
    },
  })
  return String(data?.url || '')
}

export async function syncBookingExternalCalendars(payload: { provider_type: 'BUSINESS' | 'PRO'; provider_public_id: string; days?: number }): Promise<{ busy_blocks_written: number }> {
  const { data } = await api.post<{ busy_blocks_written: number }>('/api/booking/external-calendars/sync/', payload)
  return data
}

export async function disconnectBookingExternalCalendar(connectionId: number): Promise<ExternalCalendarConnection> {
  const { data } = await api.post<ExternalCalendarConnection>(`/api/booking/external-calendars/${connectionId}/disconnect/`, {})
  return data
}

export async function updateBusinessBookingSettings(slug: string, payload: BookingSettingsUpdatePayload): Promise<BookingSettings> {
  const { data } = await api.patch<BookingSettings>(`/api/businesses/${encodeURIComponent(slug)}/booking/settings/`, payload)
  return data
}

export async function listBusinessUpcomingBookings(slug: string, days = 14): Promise<Booking[]> {
  const { data } = await api.get<BookingListResponse>(`/api/businesses/${encodeURIComponent(slug)}/booking/manage/upcoming/`, { params: { days } })
  return Array.isArray(data?.results) ? data.results : []
}

export async function startBusinessStripeConnect(slug: string): Promise<StripeConnectStartResponse> {
  const { data } = await api.post<StripeConnectStartResponse>(`/api/businesses/${encodeURIComponent(slug)}/stripe/connect/start/`, {})
  return data
}

export async function businessStripeConnectStatus(slug: string): Promise<StripeConnectStatus> {
  const { data } = await api.get<StripeConnectStatus>(`/api/businesses/${encodeURIComponent(slug)}/stripe/connect/status/`)
  return data
}

export async function getProBookingSettings(handle: string): Promise<BookingSettings> {
  const { data } = await api.get<BookingSettings>(`/api/pros/${encodeURIComponent(handle)}/booking/settings/`)
  return data
}

export async function updateProBookingSettings(handle: string, payload: BookingSettingsUpdatePayload): Promise<BookingSettings> {
  const { data } = await api.patch<BookingSettings>(`/api/pros/${encodeURIComponent(handle)}/booking/settings/`, payload)
  return data
}

export async function listProUpcomingBookings(handle: string, days = 14): Promise<Booking[]> {
  const { data } = await api.get<BookingListResponse>(`/api/pros/${encodeURIComponent(handle)}/booking/manage/upcoming/`, { params: { days } })
  return Array.isArray(data?.results) ? data.results : []
}

export async function startProStripeConnect(handle: string): Promise<StripeConnectStartResponse> {
  const { data } = await api.post<StripeConnectStartResponse>(`/api/pros/${encodeURIComponent(handle)}/stripe/connect/start/`, {})
  return data
}

export async function proStripeConnectStatus(handle: string): Promise<StripeConnectStatus> {
  const { data } = await api.get<StripeConnectStatus>(`/api/pros/${encodeURIComponent(handle)}/stripe/connect/status/`)
  return data
}

export async function listBusinessBookingServices(slug: string): Promise<BookingService[]> {
  const { data } = await api.get<{ results: BookingService[] }>(`/api/businesses/${encodeURIComponent(slug)}/booking/services/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function listBusinessPublicBookingServices(slug: string): Promise<BookingService[]> {
  const { data } = await publicApi.get<{ results: BookingService[] }>(`/api/businesses/${encodeURIComponent(slug)}/booking/services/public/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function createBusinessBookingService(slug: string, payload: BookingServiceUpsertPayload): Promise<BookingService> {
  const { data } = await api.post<BookingService>(`/api/businesses/${encodeURIComponent(slug)}/booking/services/`, payload)
  return data
}

export async function updateBusinessBookingService(slug: string, serviceId: number, payload: BookingServiceUpsertPayload): Promise<BookingService> {
  const { data } = await api.patch<BookingService>(`/api/businesses/${encodeURIComponent(slug)}/booking/services/${serviceId}/`, payload)
  return data
}

export async function disableBusinessBookingService(slug: string, serviceId: number): Promise<BookingService> {
  const { data } = await api.post<BookingService>(`/api/businesses/${encodeURIComponent(slug)}/booking/services/${serviceId}/disable/`, {})
  return data
}

export async function listProBookingServices(handle: string): Promise<BookingService[]> {
  const { data } = await api.get<{ results: BookingService[] }>(`/api/pros/${encodeURIComponent(handle)}/booking/services/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function listProPublicBookingServices(handle: string): Promise<BookingService[]> {
  const { data } = await publicApi.get<{ results: BookingService[] }>(`/api/pros/${encodeURIComponent(handle)}/booking/services/public/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function createProBookingService(handle: string, payload: BookingServiceUpsertPayload): Promise<BookingService> {
  const { data } = await api.post<BookingService>(`/api/pros/${encodeURIComponent(handle)}/booking/services/`, payload)
  return data
}

export async function updateProBookingService(handle: string, serviceId: number, payload: BookingServiceUpsertPayload): Promise<BookingService> {
  const { data } = await api.patch<BookingService>(`/api/pros/${encodeURIComponent(handle)}/booking/services/${serviceId}/`, payload)
  return data
}

export async function disableProBookingService(handle: string, serviceId: number): Promise<BookingService> {
  const { data } = await api.post<BookingService>(`/api/pros/${encodeURIComponent(handle)}/booking/services/${serviceId}/disable/`, {})
  return data
}

export async function listBusinessBookingStaff(slug: string): Promise<BookingStaffMember[]> {
  const { data } = await api.get<{ results: BookingStaffMember[] }>(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function updateBusinessBookingStaff(slug: string, staffId: number, payload: Partial<Pick<BookingStaffMember, 'is_active' | 'is_public' | 'role'>>): Promise<BookingStaffMember> {
  const { data } = await api.patch<BookingStaffMember>(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/`, payload)
  return data
}

export async function removeBusinessBookingStaff(slug: string, staffId: number): Promise<void> {
  await api.delete(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/`)
}

export async function addBusinessBookingStaff(
  slug: string,
  payload: { user_lookup?: string; membership_id?: number; is_active?: boolean; is_public?: boolean; role?: string },
): Promise<BookingStaffMember> {
  const { data } = await api.post<BookingStaffMember>(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/`, payload)
  return data
}

export async function getBusinessStaffHours(slug: string, staffId: number): Promise<BookingStaffHour[]> {
  const { data } = await api.get<{ results: BookingStaffHour[] }>(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/hours/manage/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function replaceBusinessStaffHours(slug: string, staffId: number, hours: Array<{ weekday: number; opens_at: string; closes_at: string }>): Promise<BookingStaffHour[]> {
  const { data } = await api.post<{ results: BookingStaffHour[] }>(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/hours/manage/`, { hours })
  return Array.isArray(data?.results) ? data.results : []
}

export async function getBusinessStaffSpecialHours(slug: string, staffId: number): Promise<BookingStaffSpecialHour[]> {
  const { data } = await api.get<{ results: BookingStaffSpecialHour[] }>(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/special-hours/manage/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function upsertBusinessStaffSpecialHour(
  slug: string,
  staffId: number,
  payload: { date: string; is_closed: boolean; opens_at?: string | null; closes_at?: string | null; note?: string },
): Promise<BookingStaffSpecialHour> {
  const { data } = await api.post<BookingStaffSpecialHour>(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/special-hours/manage/`, payload)
  return data
}

export async function deleteBusinessStaffSpecialHour(slug: string, staffId: number, specialId: number): Promise<void> {
  await api.delete(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/special-hours/manage/${specialId}/`)
}

export async function getBusinessStaffTimeOff(slug: string, staffId: number): Promise<BookingStaffTimeOff[]> {
  const { data } = await api.get<{ results: BookingStaffTimeOff[] }>(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/timeoff/manage/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function createBusinessStaffTimeOff(
  slug: string,
  staffId: number,
  payload: { starts_at: string; ends_at: string; note?: string },
): Promise<BookingStaffTimeOff> {
  const { data } = await api.post<BookingStaffTimeOff>(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/timeoff/manage/`, payload)
  return data
}

export async function deleteBusinessStaffTimeOff(slug: string, staffId: number, timeoffId: number): Promise<void> {
  await api.delete(`/api/businesses/${encodeURIComponent(slug)}/booking/staff/${staffId}/timeoff/manage/${timeoffId}/`)
}

export async function listProBookingStaff(handle: string): Promise<BookingStaffMember[]> {
  const { data } = await api.get<{ results: BookingStaffMember[] }>(`/api/pros/${encodeURIComponent(handle)}/booking/staff/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function removeProBookingStaff(handle: string, staffId: number): Promise<void> {
  await api.delete(`/api/pros/${encodeURIComponent(handle)}/booking/staff/${staffId}/`)
}

export async function getProStaffHours(handle: string, staffId: number): Promise<BookingStaffHour[]> {
  const { data } = await api.get<{ results: BookingStaffHour[] }>(`/api/pros/${encodeURIComponent(handle)}/booking/staff/${staffId}/hours/manage/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function replaceProStaffHours(handle: string, staffId: number, hours: Array<{ weekday: number; opens_at: string; closes_at: string }>): Promise<BookingStaffHour[]> {
  const { data } = await api.post<{ results: BookingStaffHour[] }>(`/api/pros/${encodeURIComponent(handle)}/booking/staff/${staffId}/hours/manage/`, { hours })
  return Array.isArray(data?.results) ? data.results : []
}

export async function getProStaffSpecialHours(handle: string, staffId: number): Promise<BookingStaffSpecialHour[]> {
  const { data } = await api.get<{ results: BookingStaffSpecialHour[] }>(`/api/pros/${encodeURIComponent(handle)}/booking/staff/${staffId}/special-hours/manage/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function upsertProStaffSpecialHour(
  handle: string,
  staffId: number,
  payload: { date: string; is_closed: boolean; opens_at?: string | null; closes_at?: string | null; note?: string },
): Promise<BookingStaffSpecialHour> {
  const { data } = await api.post<BookingStaffSpecialHour>(`/api/pros/${encodeURIComponent(handle)}/booking/staff/${staffId}/special-hours/manage/`, payload)
  return data
}

export async function deleteProStaffSpecialHour(handle: string, staffId: number, specialId: number): Promise<void> {
  await api.delete(`/api/pros/${encodeURIComponent(handle)}/booking/staff/${staffId}/special-hours/manage/${specialId}/`)
}

export async function getProStaffTimeOff(handle: string, staffId: number): Promise<BookingStaffTimeOff[]> {
  const { data } = await api.get<{ results: BookingStaffTimeOff[] }>(`/api/pros/${encodeURIComponent(handle)}/booking/staff/${staffId}/timeoff/manage/`)
  return Array.isArray(data?.results) ? data.results : []
}

export async function createProStaffTimeOff(
  handle: string,
  staffId: number,
  payload: { starts_at: string; ends_at: string; note?: string },
): Promise<BookingStaffTimeOff> {
  const { data } = await api.post<BookingStaffTimeOff>(`/api/pros/${encodeURIComponent(handle)}/booking/staff/${staffId}/timeoff/manage/`, payload)
  return data
}

export async function deleteProStaffTimeOff(handle: string, staffId: number, timeoffId: number): Promise<void> {
  await api.delete(`/api/pros/${encodeURIComponent(handle)}/booking/staff/${staffId}/timeoff/manage/${timeoffId}/`)
}
